import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { jwtDecode } from "jwt-decode";
import { useKronicleAccount, useKronicleAuth } from "../../auth";

const SIGNIN_URL = process.env.REACT_APP_SIGNIN_URL || "https://signin.lexops.co/";

export const getStoredOrCognitoToken = () => {
  if (typeof window === "undefined" || !window.localStorage) return null;

  let token = localStorage.getItem("token");
  if (token) return token;

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (key.endsWith(".idToken") || key.endsWith(".accessToken"))) {
      const val = localStorage.getItem(key);
      if (val) {
        localStorage.setItem("token", val);
        return val;
      }
    }
  }
  return null;
};

const AccountContext = createContext();

export function useAccount() {
  const ctx = useContext(AccountContext);
  return ctx;
}

export default function AccountProvider({ children }) {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userData, setUserData] = useState({});
  const [userDetails, setUserDetails] = useState({});

  const { handleLogin: handleLoginLib } = useKronicleAuth();
  const { logout: handleLogoutLib, accessToken, forceRefreshToken } = useKronicleAccount();

  const syncUserFromToken = useCallback(() => {
    const token = getStoredOrCognitoToken();
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setIsLoggedIn(true);
        setUserData(decoded);
        setUserDetails(decoded);
        if (decoded?.sub) {
          localStorage.setItem("user_id", decoded.sub);
        }
      } catch (e) {
        console.error("Invalid token:", e);
        localStorage.removeItem("token");
        setIsLoggedIn(false);
        setUserData({});
        setUserDetails({});
      }
    } else {
      setIsLoggedIn(false);
      setUserData({});
      setUserDetails({});
    }
  }, []);

  useEffect(() => {
    if (accessToken && !localStorage.getItem("token")) {
      localStorage.setItem("token", accessToken);
    }
    syncUserFromToken();
  }, [accessToken, syncUserFromToken]);

  const handleLogin = async () => {
    try {
      const loginData = await handleLoginLib();
      const errorMessage = loginData?.message || "";
      if (errorMessage === "There is already a signed in user.") {
        const hasToken = !!getStoredOrCognitoToken();
        if (hasToken) {
          window.location.href = "/auth/callback";
        } else {
          localStorage.clear();
          sessionStorage.clear();
          await handleLoginLib();
        }
      }
    } catch (err) {
      console.error("Error during handleLogin:", err);
    }
  };

  const handleSignup = () => {
    const homepageUrl = encodeURIComponent(`${window.location.origin}/`);
    window.location.href = `${SIGNIN_URL}register/?redirect=${homepageUrl}`;
  };

  const logout = (triggerSSOLogout = false) => {
    localStorage.removeItem("token");
    localStorage.removeItem("user_id");
    setIsLoggedIn(false);
    setUserData({});
    setUserDetails({});
    if (triggerSSOLogout && handleLogoutLib) {
      handleLogoutLib();
    }
  };

  const values = {
    handleLogin,
    handleSignup,
    logout,
    isLoggedIn,
    setIsLoggedIn,
    userData,
    setUserData,
    userDetails,
    setUserDetails,
    accessToken,
    forceRefreshToken,
    syncUserFromToken,
  };

  return (
    <AccountContext.Provider value={values}>
      {children}
    </AccountContext.Provider>
  );
}
